import React, { useEffect, useState } from 'react'
import PageLoader from '../../shared/PageLoader'
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

import { 
  Box, Button, FormControl, IconButton, 
  InputAdornment, InputLabel, MenuItem, Modal, OutlinedInput, 
  Select as MSelect, Table, TableBody, TableCell, TableContainer, TableHead, 
  TablePagination, TableRow, TableSortLabel, Typography, Chip, 
  Popover
} from '@mui/material';

import { IoMdClose, IoMdSearch } from "react-icons/io";
import Icon from '../../common/Icon';
import {  downloadWithPresignedUrl } from '../../../utils/common';
import { ERROR_MESSAGES, ESTABLISHMENT_TYPE } from '../../../utils/constants';
import { toast } from 'react-toastify';
import { hasUserAccess } from '../../../backend/auth';
import { USER_PRIVILEGES } from '../UserManagement/Roles/RoleConfiguration';
import {
  addStateRegisterQue,
  checkRegisterDataAvailability,
  checkClraDataAvailability
} from '../../../redux/features/stateRegisterQue.slice'

import {
  useFilters,
  useSearchConfig,
  useStateRegisterData,
  useCompanyData,
  useEmployerData
} from '../../../hooks/useRegistersQue';
import {
  formatDate,
  generateYearsList,
  MONTH_LIST,
  getNameById,
  getResetFields
} from '../../../utils/registersQue.utils';
import FormRenderer, { ComponentMapper, componentTypes, FormTemplate } from '../../common/FormRenderer';

const InputsDashboard = () => {
  const dispatch: any = useAppDispatch();
  const stateRegisterQueDetails = useAppSelector((state) => state.stateRegisterQue.stateRegisterQueDetails)
  const stateRegisterQueExcelandPdfDetails = useAppSelector((state) => state.stateRegisterQue.stateRegisterQueExcelandPdfDownloadDetails)
  
  const QUE_STATUS_DISPLAY: Record<string, string> = {
    pending: 'Pending',
    processing: 'In Progress',
    excel_processing: 'In Progress',
    pdf_processing: 'In Progress',
    pdf_processing_partially_completed: 'Partially Completed',
    pdf_processing_completed: 'Completed',
    failed: 'Failed'
  };

  // Use custom hooks
  const { filters, updateFilter, updateMultipleFilters, resetFilters, setFilters } = useFilters();
  const { searchConfig, updateSearchConfig } = useSearchConfig();
  const { fetchData, fetchWithSearch } = useStateRegisterData(filters, searchConfig);
  const {
    companies,
    associateCompanies,
    locations,
    totalStates,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations,
    fetchStates
  } = useCompanyData();
  const {
    employers,
    employerLocations,
    employerSubContractors,
    employerSubContractorsLocations,
    fetchEmployers,
    fetchEmployerLocations,
    fetchSubContractors,
    fetchSubContractorLocations
  } = useEmployerData();
  
  // Extract states from locations
  let states = (locations || []).map((each: any) => {
    const { state }: any = each.location || {};
    return state ? { name: state.name, id: state.id } : null;
  }).filter((each: any) => !!each).filter((each: any, index: any, self: any) => {
    return index === self.findIndex((t: any) => t.id === each.id);
  }) || [];
  
  // Local state for UI components
  const [downloading, setDownloading] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [selectedStateRegisterQue, setSelectedStateRegisterQue] = useState<any>({});
  const [stateRegisters, setStateRegisters] = useState<any[]>([]);
  const [showDashboardDetails, setShowDashboardDetails] = useState(true);
  const [openMoreFilters, setOpenMoreFilters] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [debugForm, setDebugForm] = useState<any>(null);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Nil-register data-availability check (Shops/Factory, single location only).
  // `checkedFor` fingerprints the exact selection the result applies to, so a
  // stale result never gets applied to a since-changed selection.
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityResult, setAvailabilityResult] = useState<{
    hasData: boolean;
    errors: string[];
    checkedFor: string;
  } | null>(null);

  // Nil-register data-availability check — CLRA. Only acted on when EVERY
  // matching Company/Employer/Sub-Contractor combination for the current
  // selection has has_data:false — generate_nil_register applies to every
  // combination a request matches, so it's only safe to set it when none of
  // the matched combinations actually have data (a mix of some-have/some-don't
  // is left alone; narrowing further via Sub Contractor/Sub Contractor
  // Location resolves that).
  const [checkingClraAvailability, setCheckingClraAvailability] = useState(false);
  const [clraAvailabilityResult, setClraAvailabilityResult] = useState<{
    hasData: boolean;
    errors: string[];
    combinationCount: number;
    checkedFor: string;
  } | null>(null);

  // Nil-register for the "no Sub Contractor" combination specifically — when
  // Sub Contractor is left blank and this Employer/Employer Location has a
  // MIX of combinations (some with a Sub Contractor that has data, one with
  // no Sub Contractor that doesn't), the check above correctly refuses to
  // offer nil (it would wipe the combination that has real data). This
  // second, narrower check targets only the no-Sub-Contractor row via the
  // no_sub_contractor flag, so that one row can be nil-registered without
  // touching its siblings.
  const [checkingClraNoSubAvailability, setCheckingClraNoSubAvailability] = useState(false);
  const [clraNoSubAvailabilityResult, setClraNoSubAvailabilityResult] = useState<{
    hasData: boolean;
    errors: string[];
    checkedFor: string;
  } | null>(null);

  // Modal styling
  const styleUploadModal = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    bgcolor: 'background.paper',
    boxShadow: 24,
  };
  
  // Redux selectors
  const formsDetails = useAppSelector((state) => state.inputModule.formsDetails)
  const addStateRegisterDetails = useAppSelector((state) => state.stateRegister.addStateRegisterDetails)
  const updateStateRegisterDetails = useAppSelector((state) => state.stateRegister.updateStateRegisterDetails)
  const deleteStateRegisterMappingDetails = useAppSelector((state) => state.stateRegister.deleteStateRegisterMappingDetails)
  const importStateRegisterMappingDetails = useAppSelector((state) => state.stateRegister.importStateRegisterMappingDetails);
  
  // Data from selectors
  const stateRegisterQue = stateRegisterQueDetails.data.items
  const stateRegisterQueCount = stateRegisterQueDetails.data.meta.total_count
  const stateRegisterQueExcelandPdf = stateRegisterQueExcelandPdfDetails.data.zipObject

  // Check if CLRA columns should be shown (CLRA filter selected OR search active)
  const showCLRAColumns = filters.establishmentType === 'CLRA' || isSearchActive
  
  // Loading state
  const loading = importStateRegisterMappingDetails.status === 'loading' || 
      deleteStateRegisterMappingDetails.status === 'loading' || 
      updateStateRegisterDetails.status === 'loading' || 
      addStateRegisterDetails.status === 'loading' || 
      formsDetails.status === 'loading' || 
      stateRegisterQueDetails.status === 'loading'
  
  const handleChangeCompany = (event: any) => {
    const selectedCompanyId = event.target.value as string;
    updateFilter('company', selectedCompanyId);

    fetchData({ ...filters, ...getResetFields('company'), company: selectedCompanyId }, undefined, { que_id_eq: searchConfig.searchInput });

    if (selectedCompanyId) {
      fetchAssociateCompanies(selectedCompanyId);
    }
  };

  // Removed: Using generic getNameById from utils instead

  const handleChangeAssociateCompany = (event: any) => {
    const selectedCompanyId = event.target.value as string;
    updateFilter('associateCompany', selectedCompanyId);

    fetchData({ ...filters, ...getResetFields('associateCompany'), associateCompany: selectedCompanyId });

    if (selectedCompanyId) {
      fetchLocations(selectedCompanyId);
      fetchStates();
    }
  };

  // Removed: Using generic getNameById from utils instead

  const handleChangeStateName = (event:any) => {
    const selectedStateId = event.target.value as string;
    // Only Location depends on State — Year/Month should persist across a
    // State change instead of getting reset.
    updateMultipleFilters({ stateName: selectedStateId, locations: [] });

    fetchData({ ...filters, stateName: selectedStateId, locations: [] });
  }

  const handleChangeLocation = (event: any) => {
    const selectedLocations = typeof event.target.value === 'string' ? [event.target.value] : event.target.value;
    // Use updateMultipleFilters (not updateFilter) — updateFilter runs the
    // generic cascade reset for "locations", which also clears Year/Month.
    // Changing which locations are selected shouldn't reset those.
    if (filters.establishmentType === 'CLRA') {
      // CLRA: changing the Company Location invalidates the dependent
      // Employer/Sub-Contractor cascade, but should not reset Year/Month.
      updateMultipleFilters({
        locations: selectedLocations,
        employer: '',
        employerLocation: '',
        subContractor: '',
        subContractorLocation: '',
      });
      fetchData({
        ...filters,
        locations: selectedLocations,
        employer: '',
        employerLocation: '',
        subContractor: '',
        subContractorLocation: '',
      });
    } else {
      updateMultipleFilters({ locations: selectedLocations });
      fetchData({ ...filters, locations: selectedLocations });
    }
    if (selectedLocations && selectedLocations.length > 0) {
      fetchEmployers({ ...filters, locations: selectedLocations }, locations);
    }
  };

  
  const handleChangeYear = (event:any) => {
    const selectedYear = event.target.value as string;
    updateFilter('year', selectedYear);

    fetchData({ ...filters, ...getResetFields('year'), year: selectedYear });
  };
  
  const handleChangeMonth = (event:any) => {
    const selectedMonth = event.target.value as string;
    updateFilter('month', selectedMonth);

    fetchData({ ...filters, month: selectedMonth });
  };

  const handleChangeEstablishmentType = (event: any) => {
    const selectedType = event.target.value;
    setIsSearchActive(false);
    updateFilter('establishmentType', selectedType);
    const resetFields = getResetFields('establishmentType');
    const cleanFilters = {
      company: '',
      associateCompany: '',
      stateName: '',
      locations: [],
      year: '',
      month: '',
      employer: '',
      employerLocation: '',
      subContractor: '',
      subContractorLocation: '',
      ...resetFields,
      establishmentType: selectedType
    };

    fetchData(cleanFilters, undefined, { que_id_eq: searchConfig.searchInput });
  }

  // Initialize data on component mount
  useEffect(() => {
    fetchCompanies();
    fetchData();
  }, []);

  // Fetch associate companies when company changes
  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
    }
  }, [filters.company]);

  // Fetch locations when associate company changes
  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      fetchStates();
    }
  }, [filters.associateCompany]);

  // Removed: Consolidated with main useEffect above

  // Removed: Consolidated with filters.company useEffect

  // Removed: Consolidated with filters.associateCompany useEffect



  useEffect(() => {
    if (stateRegisterQueDetails.status === 'succeeded') {
  //alert("this success  was hitted");
    } else if (stateRegisterQueDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [stateRegisterQueDetails.status])


  // Fetch employer-related data when filters change
  useEffect(() => {
    if (filters.locations && filters.locations.length > 0) {
      fetchEmployers(filters, locations);
    }
  }, [filters.locations]);

  useEffect(() => {
    if (filters.employer) {
      fetchEmployerLocations(filters, locations);
    }
  }, [filters.employer]);

  useEffect(() => {
    if (filters.employerLocation) {
      fetchSubContractors(filters, locations);
    }
  }, [filters.employerLocation]);

  useEffect(() => {
    if (filters.subContractor) {
      fetchSubContractorLocations(filters, locations);
    }
  }, [filters.subContractor]);

  // Nil-register data-availability check — Shops/Factory only, and only when
  // exactly one location is selected (the single-location flow). Re-runs
  // whenever any part of the selection changes.
  const singleLocationKey = filters.locations.length === 1 ? filters.locations[0] : '';
  useEffect(() => {
    if (
      filters.establishmentType === 'CLRA' ||
      filters.locations.length !== 1 ||
      !filters.company ||
      !filters.associateCompany ||
      !filters.stateName ||
      !filters.year ||
      !filters.month
    ) {
      setAvailabilityResult(null);
      return;
    }
    const checkedFor = [
      filters.company, filters.associateCompany, filters.stateName,
      singleLocationKey, filters.establishmentType, filters.year, filters.month,
    ].join('|');
    let cancelled = false;
    setCheckingAvailability(true);
    setAvailabilityResult(null); // clear any stale result while this selection's check is in flight
    dispatch(
      checkRegisterDataAvailability({
        company_id: filters.company,
        associate_company_id: filters.associateCompany,
        state_id: filters.stateName,
        location_id: singleLocationKey,
        establishment_type: filters.establishmentType,
        year: filters.year,
        month: filters.month,
      }),
    ).then((resultAction: any) => {
      if (cancelled) return;
      if (checkRegisterDataAvailability.fulfilled.match(resultAction)) {
        const data = resultAction.payload.data || {};
        const hasData = data.has_data !== false;
        setAvailabilityResult({ hasData, errors: data.errors || [], checkedFor });
        if (!hasData) {
          const msg = data.errors && data.errors.length
            ? data.errors.join(', ')
            : 'No data available for this location for the selected period.';
          toast.warn(`${msg} Generate Registers will create a Nil register.`);
        }
      } else {
        // Check failed — proceed as normal (safe default), no nil register flag.
        setAvailabilityResult({ hasData: true, errors: [], checkedFor });
      }
    }).finally(() => {
      if (!cancelled) setCheckingAvailability(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.establishmentType, filters.company, filters.associateCompany,
    filters.stateName, singleLocationKey, filters.year, filters.month,
  ]);

  // Nil-register data-availability check — CLRA. check_clra_data_availability
  // returns one entry per matching Company/Employer/Sub-Contractor combination
  // for the current filters. generate_nil_register applies to every
  // combination a request matches, so it's only safe to act on this when the
  // every matched combination actually lacks data — only fire
  // once Company, Associate Company, Location, Employer, Employer Location,
  // Year and Month are all selected (Sub Contractor / Sub Contractor Location
  // stay optional narrowing on top of that).
  useEffect(() => {
    if (
      filters.establishmentType !== 'CLRA' ||
      !filters.company ||
      !filters.associateCompany ||
      filters.locations.length !== 1 ||
      !filters.employer ||
      !filters.employerLocation ||
      !filters.year ||
      !filters.month
    ) {
      setClraAvailabilityResult(null);
      setClraNoSubAvailabilityResult(null);
      return;
    }
    const checkedFor = [
      filters.company, filters.associateCompany, filters.year, filters.month,
      filters.locations[0] || '', filters.employer, filters.employerLocation,
      filters.subContractor, filters.subContractorLocation,
    ].join('|');
    let cancelled = false;
    const params: any = {
      company_id: filters.company,
      associate_company_id: filters.associateCompany,
      establishment_type: filters.establishmentType,
      year: filters.year,
      month: filters.month,
    };
    if (filters.locations[0]) {
      const loc = locations.find((l: any) => l.location_id === filters.locations[0]);
      if (loc) {
        params.location_id = loc.location_id;
        params.company_location_mapping_id = loc.id;
      }
    }
    if (filters.employer) params.employer_id = filters.employer;
    if (filters.employerLocation) params.employer_location_id = filters.employerLocation;
    if (filters.subContractor) params.sub_contractor_id = filters.subContractor;
    if (filters.subContractorLocation) params.sub_contractor_location_id = filters.subContractorLocation;

    async function runClraCheck() {
      setCheckingClraAvailability(true);
      // Clear any stale results while this selection's check is in flight.
      setClraAvailabilityResult(null);
      setClraNoSubAvailabilityResult(null);

      const resultAction = await dispatch(checkClraDataAvailability(params));
      if (cancelled) return;

      if (!checkClraDataAvailability.fulfilled.match(resultAction)) {
        // Check failed — proceed as normal (safe default), no nil register flag.
        setCheckingClraAvailability(false);
        return;
      }

      const combinations = (resultAction.payload.data || {}).combinations || [];
      const allNoData = combinations.length > 0 && combinations.every((c: any) => c.has_data === false);

      if (allNoData) {
        const errors = Array.from(new Set(combinations.flatMap((c: any) => c.errors || []))) as string[];
        setClraAvailabilityResult({ hasData: false, errors, combinationCount: combinations.length, checkedFor });
        const msg = errors.length
          ? errors.join(', ')
          : 'No data available for the selected combination(s).';
        const registerWord = combinations.length > 1 ? `${combinations.length} Nil registers` : 'a Nil register';
        toast.warn(`${msg} Generate Registers will create ${registerWord}.`);
        setCheckingClraAvailability(false);
        return;
      }

      // Mixed results (some combinations have data, some don't) — not safe to
      // blanket-apply generate_nil_register across all of them. But if the
      // specific "no Sub Contractor" combination among them lacks data, it can
      // still be nil-registered on its own via the no_sub_contractor flag,
      // without touching its siblings that do have a Sub Contractor and data.
      const hasMixedResults = combinations.length > 1
        && combinations.some((c: any) => c.has_data === false)
        && combinations.some((c: any) => c.has_data !== false);
      const noSubEntry = combinations.find((c: any) => !c.sub_contractor_id);
      setCheckingClraAvailability(false);
      if (!hasMixedResults || !noSubEntry || noSubEntry.has_data !== false) {
        return;
      }

      setCheckingClraNoSubAvailability(true);
      const noSubResultAction = await dispatch(
        checkClraDataAvailability({ ...params, no_sub_contractor: true }),
      );
      if (cancelled) { setCheckingClraNoSubAvailability(false); return; }
      if (checkClraDataAvailability.fulfilled.match(noSubResultAction)) {
        const noSubCombinations = (noSubResultAction.payload.data || {}).combinations || [];
        if (noSubCombinations.length === 1 && noSubCombinations[0].has_data === false) {
          const combo = noSubCombinations[0];
          setClraNoSubAvailabilityResult({ hasData: false, errors: combo.errors || [], checkedFor });
          const msg = combo.errors && combo.errors.length
            ? combo.errors.join(', ')
            : 'No data available for this combination without a Sub Contractor.';
          toast.warn(`${msg} Generate Registers will create a Nil register for this combination only.`);
        }
      }
      setCheckingClraNoSubAvailability(false);
    }

    runClraCheck();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.establishmentType, filters.company, filters.associateCompany,
    filters.year, filters.month, filters.locations, filters.employer,
    filters.employerLocation, filters.subContractor, filters.subContractorLocation,
  ]);

  function handleChangeEmployer(event: any) {
    const selectedEmployer = event.target.value as string;
    updateFilter('employer', selectedEmployer);

    fetchData({ ...filters, ...getResetFields('employer'), employer: selectedEmployer });

    if (selectedEmployer) {
      fetchEmployerLocations({ ...filters, ...getResetFields('employer'), employer: selectedEmployer }, locations);
    }
  }

  function handleChangeEmployerLocation(event: any) {
    const selectedLocation = event.target.value as string;
    updateFilter('employerLocation', selectedLocation);

    fetchData({ ...filters, ...getResetFields('employerLocation'), employerLocation: selectedLocation });

    if (selectedLocation) {
      fetchSubContractors({ ...filters, ...getResetFields('employerLocation'), employerLocation: selectedLocation }, locations);
    }
  }

  function handleChangeSubContractor(event: any) {
    const selectedSubContractor = event.value as string;
    updateFilter('subContractor', selectedSubContractor);
    
    if (selectedSubContractor) {
      fetchSubContractorLocations({ ...filters, subContractor: selectedSubContractor }, locations);
    }
  }

  function handleChangeSubContractorLocation(event: any) {
    const selectedLocation = event.value as string;
    updateFilter('subContractorLocation', selectedLocation);
  }

  function applyMoreFilters() {
    setOpenMoreFilters(false);
    fetchData();
  }

  function clearMoreFilters() {
    setDebugForm(null);
    updateMultipleFilters({
      "subContractor": "",
      "subContractorLocation": ""
    });
    setTimeout(() => {
      setOpenMoreFilters(false);
      setAnchorEl(null);
      fetchData({...filters, subContractor: "", subContractorLocation: ""});
    }, 100);
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(undefined, { page: newPage });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({ rowsPerPage: newRowsPerPage, page: 0 });
    fetchData(undefined, { rowsPerPage: newRowsPerPage, page: 0 });
  };

  const onClickSortCompany = () => {
    const newSortType = searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({ activeSort: 'company_id', sortType: newSortType });
    fetchData(filters, undefined, {}, 'company_id', newSortType);
  }

  const onClickSortAssociateCompany = () => {
    const newSortType = searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({ activeSort: 'associate_company_id', sortType: newSortType });
    fetchData(filters, undefined, {}, 'associate_company_id', newSortType);
  }

  const onClickSortLocation = () => {
    const newSortType = searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({ activeSort: 'location_id', sortType: newSortType });
    fetchData(filters, undefined, {}, 'location_id', newSortType);
  }

  const onClickSortYear = () => {
    const newSortType = searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({ activeSort: 'year', sortType: newSortType });
    fetchData(filters, undefined, {}, 'year', newSortType);
  }


  // Use utility functions for lists
  const yearsList = generateYearsList();
  const monthList = MONTH_LIST;
  const EstablishmentTypesList = Object.values(ESTABLISHMENT_TYPE);

  // Backend no longer returns per-location/per-employer detail in any
  // response — that breakdown always goes out by email to the triggering
  // user. Every path here just surfaces the backend's own `message` (or an
  // `errors[]`/`error` on failure), it never reconstructs per-item detail.
  const buildErrorMessage = (payload: any): React.ReactNode => {
    const errorData = payload || {};
    if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
      return (
        <div>
          {errorData.errors.map((err: string, i: number) => (
            <div key={i}>{err}</div>
          ))}
        </div>
      );
    }
    return errorData.error || errorData.message || "An error occurred while processing the registers.";
  };

  // Nil registers can only be requested for a single, unambiguous selection
  // (one location for Shops/Factory, one resolved combination for CLRA) — so
  // whenever the backend's raw "no data available" message comes back for a
  // broader/ambiguous selection, replace it with copy that tells the user
  // what to do about it instead of just repeating the raw error. Applies to
  // every branch (CLRA and Shops/Factory alike).
  const NO_DATA_MESSAGE_PATTERN = /no data available to proceed for register generation/i;
  const NO_DATA_FRIENDLY_MESSAGE = 'No data uploaded for the selected locations. Select only one location at a time to generate the NIL registers.';
  const NO_DATA_FRIENDLY_MESSAGE_CLRA = 'No data uploaded for the selected location. Select all the filters to generate the NIL register.';
  const getFriendlyErrorMessage = (payload: any, isClra: boolean = false): React.ReactNode => {
    const errorData = payload || {};
    const rawMessage = String(errorData.error || errorData.message || '');
    if (NO_DATA_MESSAGE_PATTERN.test(rawMessage)) {
      return isClra ? NO_DATA_FRIENDLY_MESSAGE_CLRA : NO_DATA_FRIENDLY_MESSAGE;
    }
    return buildErrorMessage(payload);
  };
  // A "0 created" response is technically a success (HTTP-wise) but means the
  // same thing as the no-data error above for a broad/ambiguous CLRA
  // selection — surface it the same way instead of a green success toast.
  const isZeroCreatedMessage = (data: any): boolean => {
    if (!data) return false;
    if (data.created_count === 0 || data.count === 0) return true;
    return /(^|\D)0\s+state register queue/i.test(String(data.message || ''));
  };

  const handleProcessRegisters = async () => {
    setDownloading(true);
    try {
      const establishmentType = filters.establishmentType;

      // ── CLRA — Location/Employer/Sub Contractor are all optional. The
      // backend sizes the request by how many Company/Employer/Sub-Contractor
      // combinations match whatever filters were given, then decides sync
      // (≤50 matches — counts) vs async (51+ — background + email) itself. ──
      if (establishmentType === 'CLRA') {
        const payload: any = {
          state_register_que: {
            company_id: filters.company,
            associate_company_id: filters.associateCompany,
            year: filters.year,
            month: filters.month,
            establishment_type: establishmentType
          }
        };
        if (filters.locations[0]) {
          const loc = locations.find((l: any) => l.location_id === filters.locations[0]);
          if (loc) {
            payload.state_register_que.location_id = loc.location_id;
            payload.state_register_que.company_location_mapping_id = loc.id;
            payload.state_register_que.state_id = loc.location && loc.location.state ? loc.location.state.id : undefined;
          }
        }
        if (filters.employer) payload.state_register_que.employer_id = filters.employer;
        if (filters.employerLocation) payload.state_register_que.employer_location_id = filters.employerLocation;
        if (filters.subContractor) payload.state_register_que.sub_contractor_id = filters.subContractor;
        if (filters.subContractorLocation) payload.state_register_que.sub_contractor_location_id = filters.subContractorLocation;

        // If the availability check already found that every matching
        // Company/Employer/Sub-Contractor combination for this exact
        // selection has no data, silently ask for Nil register(s) instead of
        // failing.
        const clraCheckedFor = [
          filters.company, filters.associateCompany, filters.year, filters.month,
          filters.locations[0] || '', filters.employer, filters.employerLocation,
          filters.subContractor, filters.subContractorLocation,
        ].join('|');
        const isClraNil = !!(
          clraAvailabilityResult &&
          clraAvailabilityResult.checkedFor === clraCheckedFor &&
          !clraAvailabilityResult.hasData
        );
        // Narrower case: the "no Sub Contractor" combination specifically has
        // no data (while sibling combinations that do have a Sub Contractor
        // are left untouched) — scope the request to just that row.
        const isClraNoSubNil = !!(
          clraNoSubAvailabilityResult &&
          clraNoSubAvailabilityResult.checkedFor === clraCheckedFor &&
          !clraNoSubAvailabilityResult.hasData
        );
        if (isClraNil || isClraNoSubNil) {
          payload.state_register_que.generate_nil_register = true;
        }
        if (isClraNoSubNil) {
          payload.state_register_que.no_sub_contractor = true;
        }

        const resultAction = await dispatch(addStateRegisterQue({ payload, establishmentType }));
        if (addStateRegisterQue.fulfilled.match(resultAction)) {
          const data = resultAction.payload.data || {};
          const message = data.message || 'Queue creation started.';
          if (!(isClraNil || isClraNoSubNil) && isZeroCreatedMessage(data)) {
            toast.error(NO_DATA_FRIENDLY_MESSAGE_CLRA);
          } else {
            toast.success((isClraNil || isClraNoSubNil) ? `Nil Register — ${message}` : message);
          }
          fetchData();
        } else {
          toast.error(getFriendlyErrorMessage(resultAction.payload, true));
        }
        setDownloading(false);
        return;
      }

      // ── Shops/Factory ──
      if (filters.locations.length === 0) {
        // Case 1 — no location selected: backend auto-expands to every
        // location assigned under this company/associate company/state.
        // Always async now, regardless of how many locations that resolves
        // to — no counts up front, full breakdown goes out by email.
        const payload = {
          state_register_que: {
            company_id: filters.company,
            associate_company_id: filters.associateCompany,
            state_id: filters.stateName,
            year: filters.year,
            month: filters.month,
            establishment_type: establishmentType
          }
        };
        const resultAction = await dispatch(addStateRegisterQue({ payload, establishmentType }));
        if (addStateRegisterQue.fulfilled.match(resultAction)) {
          const data = resultAction.payload.data || {};
          toast.success(data.message || "Queue creation started in the background — you'll receive an email with the results shortly.");
          fetchData();
        } else {
          toast.error(getFriendlyErrorMessage(resultAction.payload));
        }
      } else if (filters.locations.length === 1) {
        // Case 2 — exactly one location: standard single-queue create. If the
        // data-availability check already found no data for this exact
        // selection, silently ask for a Nil register instead of failing.
        const locationId = filters.locations[0];
        const checkedFor = [
          filters.company, filters.associateCompany, filters.stateName,
          locationId, establishmentType, filters.year, filters.month,
        ].join('|');
        const shouldGenerateNil = !!(
          availabilityResult &&
          availabilityResult.checkedFor === checkedFor &&
          !availabilityResult.hasData
        );
        const payload: any = {
          state_register_que: {
            company_id: filters.company,
            associate_company_id: filters.associateCompany,
            location_id: locationId,
            state_id: filters.stateName,
            year: filters.year,
            month: filters.month,
            establishment_type: establishmentType
          }
        };
        if (shouldGenerateNil) {
          payload.state_register_que.generate_nil_register = true;
        }
        const resultAction = await dispatch(addStateRegisterQue({ payload, establishmentType }));
        if (addStateRegisterQue.fulfilled.match(resultAction)) {
          const data = resultAction.payload.data || {};
          const nilPrefix = shouldGenerateNil ? 'Nil Register — ' : '';
          if (data.que_id) {
            toast.success(`${nilPrefix}Que Number ${data.que_id} received.`);
          } else {
            toast.success(`${nilPrefix}${data.message || 'Register queue created successfully.'}`);
          }
          fetchData();
        } else {
          toast.error(getFriendlyErrorMessage(resultAction.payload));
        }
      } else {
        // Case 3 — multiple locations: one batched request with location_ids.
        // Backend decides sync (≤50 — created/skipped counts) vs async
        // (51+ — background + email) itself; either way we just show its message.
        const payload = {
          state_register_que: {
            company_id: filters.company,
            associate_company_id: filters.associateCompany,
            state_id: filters.stateName,
            year: filters.year,
            month: filters.month,
            establishment_type: establishmentType,
            location_ids: filters.locations
          }
        };
        const resultAction = await dispatch(addStateRegisterQue({ payload, establishmentType }));
        if (addStateRegisterQue.fulfilled.match(resultAction)) {
          const data = resultAction.payload.data || {};
          toast.success(data.message || 'Queue creation started.');
          fetchData();
        } else {
          toast.error(getFriendlyErrorMessage(resultAction.payload));
        }
      }
      setDownloading(false);
    } catch (error) {
      console.error("Error processing registers:", error);
      toast.error("An error occurred while processing the registers.");
      setDownloading(false); // Reset the downloading state even if there's an error
    }
  };

  // Moved interfaces to utils if needed globally

  const onClickDownloadZip = (url:any) => {
    if(url) {
      downloadWithPresignedUrl(url.split('/').pop(), url)
    } else {
      toast.info("No URL found");
    }
  }

  const onclickDownloadExcel=(file:any)=>{
    console.log(file);
    const url = file.register_excel_url || '';
    if (url) {
      downloadWithPresignedUrl(url.split('/').pop(), url);
    } else {
      toast.info("No URL found");
      return "No URL found";
    }
  }

  const onclickDownloadPdf=(file:any)=>{
    console.log(file);
    const url = file.register_pdf_url || '';
    if (url) {
      downloadWithPresignedUrl(url.split('/').pop(), url);
    } else {
      toast.info("No URL found");
      return "No URL found";
    }
  }

  // Function to handle the download of all files
  // const handleFileDownloads = async (files: FileDetails[]) => {
  //   for (const file of files) {
  //    // alert(file.filePath);
  //     await onclickDownloadExcel(file);
  //   }
  // };

  const onclickDownloadBoth=(each :any)=>{
    setOpenDownloadModal(true);
    console.log(each);
    /*
    const Payload: any =  { 
      company_id: each.company_id,
      associate_company_id:  each.associate_company_id,
      location_id: each.location_id,
      state_id: each.state_id, 
      year: each.year, 
      month: each.month,
      establishment_type : each.establishment_type,
      zipType : "ExcelZip"
    }
    */
    //dispatch(getstateRegisterQueExcelandPdfDownloadDetails(Payload))
    setSelectedStateRegisterQue(each);
    const _stateRegisters = each.state_registers || [];
    const updatedStateRegisters: any = [];
    _stateRegisters.forEach((_stateRegister: any) => {
      const _registers = [...(_stateRegister.registers || [])].sort((a: any, b: any) => Number(a.id) - Number(b.id));
      if(_registers.length > 1) {
        updatedStateRegisters.push(
          ..._registers.map((register: any, index: number) => {
            return {
              ..._stateRegister,
              form_name: _stateRegister.form_name + ` Part - ${index + 1}`,
              register_excel_url: register.excel_url || '',
              register_pdf_url: register.pdf_url || ''
            }
          })
        );
      } else {
        updatedStateRegisters.push({
          ..._stateRegister,
          register_excel_url: (_registers[0] || {}).excel_url || '',
          register_pdf_url: (_registers[0] || {}).pdf_url || ''
        });
      }
    });
    setStateRegisters(updatedStateRegisters);
  }

  // Using formatDate from utils

  const handleChangeSearchInput = (event: any) => {
    updateSearchConfig({ searchInput: event.target.value });
  }

  const onClickClearSearch = () => {
    setIsSearchActive(false);
    updateSearchConfig({ searchInput: '' });
    fetchData();
  }

  const onClickSearch = () => {
    setIsSearchActive(true);
    updateSearchConfig({ page: 0 });
    fetchWithSearch(searchConfig.searchInput);
  }

  const moreFiltersSchema: any = {
    fields: [
      {
        component: componentTypes.SELECT,
        name: 'subContractor',
        label: 'Sub Contractor',
        options: employerSubContractors.map((x: any) => ({
            label: x.name,
            value: x.id
        })),
        initialValue: (debugForm || {}).subContractor,
        isMulti: false,
        isDisabled: !filters.employerLocation,
        isLoading: !employerSubContractors,
        onChange: handleChangeSubContractor.bind(this)
      },
      {
        component: componentTypes.SELECT,
        name: 'subContractorLocation',
        label: 'Sub Contractor Location',
        options: employerSubContractorsLocations.map((x: any) => ({
            label: x.location_name,
            value: x.id
        })),
        initialValue: (debugForm || {}).subContractorLocation,
        isMulti: false,
        isDisabled: !filters.subContractor,
        isLoading: !employerSubContractorsLocations,
        onChange: handleChangeSubContractorLocation.bind(this)
      }
    ]
  }

  return (
    <div style={{ backgroundColor:'#ffffff', minHeight:'100vh'}}>
      {/* Upload Modal */}
      <Modal
        open={openDownloadModal}
        onClose={() => {setDownloading(false)}}
      >
        <Box sx={styleUploadModal}>
          <Box sx={{backgroundColor:'#E2E3F8', padding:'10px', px:'20px', borderRadius:'6px', boxShadow: '0px 6px 10px #CDD2D9', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <Typography sx={{font: 'normal normal normal 32px/40px Calibri'}}>Excel & Pdf Download</Typography>
            <IconButton
              onClick={() => {setOpenDownloadModal(false);}}
            >
              <IoMdClose />
            </IconButton>
          </Box>

          <TableContainer sx={{ border: '1px solid #e6e6e6', marginTop: '10px', maxHeight: '385px', overflowY: 'scroll' }}>
              <Table stickyHeader sx={{ minWidth: 650 }} aria-label="sticky table">
                <TableHead sx={{ '.MuiTableCell-root': { backgroundColor: '#E7EEF7',fontWeight:'600' } }}>
                  <TableRow>
                    {/* <TableCell><Checkbox/></TableCell> */}
                    <TableCell > <TableSortLabel active={searchConfig.activeSort === 'company_id'} direction={searchConfig.sortType} onClick={onClickSortCompany}>FormName</TableSortLabel></TableCell>
                    <TableCell > 
                      <TableSortLabel active={searchConfig.activeSort === 'associate_company_id'} direction={searchConfig.sortType} onClick={onClickSortAssociateCompany}>Download Excel</TableSortLabel>
                    </TableCell>
                    <TableCell >Download Pdf</TableCell>
                    <TableCell >First Generated Date</TableCell>
                    <TableCell >Last Generated Date</TableCell>
                    <TableCell >Total Generations</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>

                  {stateRegisters && stateRegisters.map((each: any, index: number) => (
                    <TableRow
                      key={index}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      {/* <TableCell><Checkbox/></TableCell> */}
                      <TableCell >{each.form_name ? each.form_name : 'NA'}</TableCell>
                      <TableCell >
                        <Box sx={{ display: 'flex',justifyContent: "center" }}>
                          {each.register_excel_url && <Icon 
                              style={{ color: '#0654AD' }} 
                              type="button" 
                              name={'download'} 
                              text={'download'}  
                              action={() => onclickDownloadExcel(each)}
                          />}
                        </Box>
                      </TableCell>
                      
                      <TableCell >
                        <Box sx={{ display: 'flex',justifyContent: "center" }}>
                          {each.register_pdf_url && <Icon 
                              style={{ color: '#0654AD' }} 
                              type="button" 
                              name={'download'} 
                              text={'download'}  
                              action={() => onclickDownloadPdf(each)}
                          />}
                        </Box>
                      </TableCell>
                      <TableCell >{formatDate(each.created_at)}</TableCell>
                      <TableCell >{formatDate(each.updated_at)}</TableCell>
                      <TableCell >{each.count}</TableCell>

                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

          <Box sx={{display:'flex', padding:'20px', borderTop:'1px solid #6F6F6F',justifyContent:'flex-end', alignItems:'center', mt:4}}>
            <Button variant='contained' sx={{backgroundColor:'#707070',mr:10}} onClick={() => {onClickDownloadZip(selectedStateRegisterQue.register_url)}}>Download Excel Zip</Button>
            <Button variant='contained' sx={{backgroundColor:'#707070',mr:10}} onClick={() => {onClickDownloadZip(selectedStateRegisterQue.pdf_register_url)}}>Download PDF Zip</Button>
            <Button variant='contained' sx={{backgroundColor:'#707070'}} onClick={() => {setOpenDownloadModal(false)}}>Cancel</Button>
          </Box>
        </Box>
      </Modal>
      <div style={{paddingBottom:'100px'}}>

            {/* Filter Box*/}
            <Box sx={{paddingX: '20px', paddingY:'10px',}}>
              <div style={{backgroundColor:'#E2E3F8', padding:'20px', borderRadius:'6px', boxShadow: '0px 6px 10px #CDD2D9'}}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', marginTop: '10px' }}>
                    <h5 style={{ font: 'normal normal normal 32px/40px Calibri', margin: 0 }}>Que Registers</h5>
                    <Box sx={{ width: '100%', maxWidth: '360px', textAlign: 'right' }}>
                      <FormControl sx={{ width: '100%', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <InputLabel htmlFor="outlined-adornment-search">Search Que No</InputLabel>
                        <OutlinedInput
                          value={searchConfig.searchInput}
                          onChange={handleChangeSearchInput}
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          id="outlined-adornment-search"
                          type='text'
                          endAdornment={
                            <InputAdornment position="end">
                              {searchConfig.searchInput &&
                                <IconButton
                                  onClick={onClickClearSearch}
                                  edge="end"
                                >
                                  <IoMdClose />
                                </IconButton>
                              }
                              <IconButton
                                onClick={onClickSearch}
                                edge="end"
                              >
                                <IoMdSearch />
                              </IconButton>
                            </InputAdornment>
                          }
                          label="Search"
                        />
                      </FormControl>
                    </Box>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '15px', marginTop: '10px' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Typography mb={1}>Establishment Type</Typography>
                      <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <MSelect
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          displayEmpty
                          value={filters.establishmentType}
                          onChange={handleChangeEstablishmentType}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                width: 150,
                                marginTop: "3px"
                              }
                            }
                          }}
                        >
                          <MenuItem disabled sx={{ display: 'none' }} value="">
                            Select Type
                          </MenuItem>
                          {EstablishmentTypesList && EstablishmentTypesList.map((each: any) =>
                            <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each}>{each}</MenuItem>
                          )}
                        </MSelect>
                      </FormControl>
                    </Box>
                    {showDashboardDetails && (
                      <Box sx={{ width: '100%', textAlign: 'right' }}>
                        <Typography mb={1}>&nbsp;</Typography>
                        {filters.establishmentType !== 'CLRA' &&
                          hasUserAccess(USER_PRIVILEGES.REGISTER_GENERATION_QUE) && (
                            <Button
                              variant='contained'
                              onClick={handleProcessRegisters}
                              disabled={
                                !filters.company || !filters.associateCompany || !filters.stateName ||
                                !filters.year || !filters.month || !filters.establishmentType ||
                                checkingAvailability
                              }
                            >
                              Generate Registers
                            </Button>
                          )}
                        {filters.establishmentType === 'CLRA' &&
                          hasUserAccess(USER_PRIVILEGES.REGISTER_GENERATION_QUE) && (
                            <Button
                              variant='contained'
                              onClick={handleProcessRegisters}
                              disabled={
                                !filters.company || !filters.associateCompany ||
                                !filters.year || !filters.month || !filters.establishmentType ||
                                checkingClraAvailability || checkingClraNoSubAvailability
                              }
                            >
                              Generate Registers
                            </Button>
                          )}
                      </Box>
                    )}
                  </div>

                  {filters.locations.length === 1 &&
                    availabilityResult &&
                    !availabilityResult.hasData && (
                      <div style={{ marginBottom: '16px', textAlign: 'right', fontSize: '12px', color: '#b45309' }}>
                        {availabilityResult.errors.length
                          ? availabilityResult.errors.join(', ')
                          : 'No data available for this location for the selected period.'}
                        {' '}Generate Registers will create a Nil register.
                      </div>
                    )}

                  {filters.establishmentType === 'CLRA' && (
                    <div style={{ marginBottom: '16px', textAlign: 'right', fontSize: '12px', color: '#6b7280' }}>
                      To generate NIL registers, select Company Location, Employer, Employer Location, Year and Month (Sub Contractor is optional).
                    </div>
                  )}

                  {filters.establishmentType === 'CLRA' &&
                    clraAvailabilityResult &&
                    !clraAvailabilityResult.hasData && (
                      <div style={{ marginBottom: '16px', textAlign: 'right', fontSize: '12px', color: '#b45309' }}>
                        {clraAvailabilityResult.errors.length
                          ? clraAvailabilityResult.errors.join(', ')
                          : 'No data available for the selected combination(s).'}
                        {' '}Generate Registers will create{' '}
                        {clraAvailabilityResult.combinationCount > 1
                          ? `${clraAvailabilityResult.combinationCount} Nil registers`
                          : 'a Nil register'}.
                      </div>
                    )}

                  {filters.establishmentType === 'CLRA' &&
                    clraNoSubAvailabilityResult &&
                    !clraNoSubAvailabilityResult.hasData && (
                      <div style={{ marginBottom: '16px', textAlign: 'right', fontSize: '12px', color: '#b45309' }}>
                        {clraNoSubAvailabilityResult.errors.length
                          ? clraNoSubAvailabilityResult.errors.join(', ')
                          : 'No data available for this combination without a Sub Contractor.'}
                        {' '}Generate Registers will create a Nil register for this combination only —{' '}
                        other Sub Contractor combinations under this Employer are unaffected.
                      </div>
                    )}

                  <div style={{ display: 'flex' }}>

                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Typography mb={1}>Company</Typography>
                      <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <MSelect
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          value={filters.company}
                          displayEmpty
                          disabled={!filters.establishmentType}
                          onChange={handleChangeCompany}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                width: 230,
                                marginTop: "3px"
                              }
                            }
                          }}
                        >
                          <MenuItem disabled sx={{ display: 'none' }} value="">
                            Select Company
                          </MenuItem>
                          {companies && companies.map((each: any) => {
                            return <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>{each.name}</MenuItem>
                          })}
                        </MSelect>
                      </FormControl>
                    </Box>

                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Typography mb={1}>Associate Company</Typography>
                      <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <MSelect
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          displayEmpty
                          value={filters.associateCompany}
                          disabled={!filters.company}
                          onChange={handleChangeAssociateCompany}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                width: 230,
                                marginTop: "3px"
                              }
                            }
                          }}
                        >
                          <MenuItem disabled sx={{ display: 'none' }} value="">
                            Select Associate Company
                          </MenuItem>
                          {associateCompanies && associateCompanies.map((each: any) => {
                            return <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>{each.name}</MenuItem>
                          })}
                        </MSelect>
                      </FormControl>
                    </Box>

                    {filters.establishmentType !== "CLRA" && 
                    <>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Typography mb={1}>State</Typography>
                        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                          <MSelect
                            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                            displayEmpty
                            value={filters.stateName}
                            disabled={!filters.associateCompany}
                            onChange={handleChangeStateName}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 200,
                                  width: 230,
                                  marginTop: "3px"
                                }
                              }
                            }}
                          >
                            <MenuItem disabled sx={{ display: 'none' }} value="">
                              Select State
                            </MenuItem>
                            {states && states.map((each: any) => {
                              return <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>{each.name}</MenuItem>
                            })}
                          </MSelect>
                        </FormControl>
                      </Box>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Typography mb={1}>Location</Typography>
                        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                          <MSelect
                            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                            displayEmpty
                            value={filters.locations}
                            multiple // Enable multiple selection
                            disabled={!filters.stateName}
                            onChange={handleChangeLocation}
                            input={<OutlinedInput />}
                            renderValue={(filters.locations || []).length > 0 ?(selected) => (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map((value: string) => {
                                  // Find the selected location by its ID
                                  const selectedLocation = locations.find((loc: any) => loc.location_id === value);
                                  // Display the location name if found
                                  return selectedLocation ? <Chip key={value} label={selectedLocation.location.name} /> : null;
                                })}
                              </Box>
                            ) : () => <>All Locations</>}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 200,
                                  width: 230,
                                  marginTop: "3px"
                                }
                              }
                            }}
                          >
                            <MenuItem disabled sx={{ display: 'none' }} value={[]}>
                              All Locations
                            </MenuItem>
                            {locations && locations.map((each: any) => {
                              const { id, name, code, state, city }: any = each.location || {};
                              if (state && state.id === filters.stateName) {
                                return <MenuItem key={each.location_id} sx={{ width: '240px', whiteSpace: 'initial' }} value={each.location_id}>{`${name} (${state && state.code}-${city && city.code}-${code})`}</MenuItem>
                              }
                            })}
                          </MSelect>
                        </FormControl>
                        {filters.stateName && (!filters.locations || filters.locations.length === 0) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            No location selected — registers will be generated for all locations in this state.
                          </Typography>
                        )}
                      </Box>
                    </>
                    }


                    {filters.establishmentType === "CLRA" && <>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Typography mb={1}>Company Location</Typography>
                        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                          <MSelect
                            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                            displayEmpty
                            value={filters.locations}
                            multiple={false}
                            disabled={!filters.associateCompany}
                            onChange={handleChangeLocation}
                            input={<OutlinedInput />}
                            renderValue={(filters.locations || []).length > 0 ? (selected) => (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map((value: string) => {
                                  // Find the selected location by its ID
                                  const selectedLocation = locations.find((loc: any) => loc.location_id === value);
                                  // Display the location name if found
                                  return selectedLocation ? selectedLocation.location.name : null;
                                })}
                              </Box>
                            ) : () => <>All Locations</>}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 200,
                                  width: 230,
                                  marginTop: "3px"
                                }
                              }
                            }}
                          >
                            <MenuItem disabled sx={{ display: 'none' }} value={[]}>
                              All Locations
                            </MenuItem>
                            {locations && locations.map((each: any) => {
                              const { id, name, code, state, city }: any = each.location || {};
                              return <MenuItem key={each.location_id} sx={{ width: '240px', whiteSpace: 'initial' }} value={each.location_id}>{`${name} (${state && state.code}-${city && city.code}-${code})`}</MenuItem>
                            })}
                          </MSelect>
                        </FormControl>
                        {filters.associateCompany && (!filters.locations || filters.locations.length === 0) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            No location selected — registers will be generated for all Company/Employer/Sub-Contractor mappings under this associate company.
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Typography mb={1}>Employer</Typography>
                        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                          <MSelect
                            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                            displayEmpty
                            value={filters.employer}
                            disabled={filters.locations && filters.locations.length === 0}
                            onChange={handleChangeEmployer}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 200,
                                  width: 230,
                                  marginTop: "3px"
                                }
                              }
                            }}
                          >
                            <MenuItem disabled sx={{ display: 'none' }} value="">
                              Select Employer
                            </MenuItem>
                            {employers && employers.map((each: any) => {
                              return <MenuItem key={each.id} sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>{each.name}</MenuItem>
                            })}
                          </MSelect>
                        </FormControl>
                      </Box>

                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Typography mb={1}>Employer Location</Typography>
                        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                          <MSelect
                            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                            displayEmpty
                            value={filters.employerLocation}
                            disabled={!filters.employer}
                            onChange={handleChangeEmployerLocation}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 200,
                                  width: 150,
                                  marginTop: "3px"
                                }
                              }
                            }}
                          >
                            <MenuItem disabled sx={{ display: 'none' }} value="">
                              Select Employer Location
                            </MenuItem>
                            {employerLocations && employerLocations.map((each: any) =>
                              <MenuItem key={each.id} sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>{each.location_name}</MenuItem>
                            )}
                          </MSelect>
                        </FormControl>
                      </Box>
                    </>}

                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Typography mb={1}>Year</Typography>
                      <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <MSelect
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          displayEmpty
                          value={filters.year}
                          disabled={filters.establishmentType === 'CLRA' ? !filters.associateCompany : !filters.stateName}
                          onChange={handleChangeYear}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                width: 150,
                                marginTop: "3px"
                              }
                            }
                          }}
                        >
                          <MenuItem disabled sx={{ display: 'none' }} value="">
                            Select Year
                          </MenuItem>
                          {yearsList && yearsList.map((each: any) =>
                            <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each}>{each}</MenuItem>
                          )}
                        </MSelect>
                      </FormControl>
                    </Box>

                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Typography mb={1}>Month</Typography>
                      <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <MSelect
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          displayEmpty
                          value={filters.month}
                          disabled={!filters.year}
                          onChange={handleChangeMonth}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                width: 150,
                                marginTop: "3px"
                              }
                            }
                          }}
                        >
                          <MenuItem disabled sx={{ display: 'none' }} value="">
                            Select Month
                          </MenuItem>
                          {monthList && monthList.map((each: any) =>
                            <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each}>{each}</MenuItem>
                          )}
                        </MSelect>
                      </FormControl>
                    </Box>

                    {filters.establishmentType === 'CLRA' && <>
                      <div className="d-flex align-items-end me-3">
                        <div className="d-flex flex-column">
                            <button type="submit" className="btn btn-primary d-flex align-items-center"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if(!filters.employer || !filters.employerLocation){
                                      return toast.error("Please select Employer and Employer Location");
                                    }
                                    setAnchorEl(e.currentTarget);
                                    setOpenMoreFilters(true);
                                }}
                                aria-describedby={openMoreFilters ? 'more-filters-popover' : undefined}
                                style={{width: '136px'}}>
                                <span className="ms-2">More Filters</span>
                            </button>
                        </div>
                      </div>

                      
                      <Popover 
                        id="more-filters-popover"
                        anchorEl={anchorEl}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'center',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        open={openMoreFilters}
                      >
                        <div className="px-3 py-2" style={{width: '500px'}}>
                          <div className="bold mb-10 d-flex justify-content-between" style={{fontSize: '18px', fontWeight: 600, lineHeight: '20px', width: '100%', borderBottom: '1px solid #F2F2F2'}}>
                            <div style={{display: 'flex', alignItems: 'center'}}>Additional Filters</div>
                            <div><IconButton onClick={() => {
                              setOpenMoreFilters(false);
                              setAnchorEl(null);
                            }}><IoMdClose /></IconButton></div>
                          </div>
                          <div className="d-flex justify-content-start">
                            <div className="col-12">
                              <form>
                                <div className="row mt-3">
                                  <div className="col-12">
                                    <FormRenderer FormTemplate={FormTemplate}
                                      initialValues={{hideButtons: true, ...debugForm}}
                                      componentMapper={ComponentMapper}
                                      schema={moreFiltersSchema}
                                      debug={(e: any) => { setDebugForm(e.values); }}
                                    />
                                  </div>
                                </div>
                              </form>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mt-4 mb-2">
                            <Button variant="outlined" onClick={clearMoreFilters} className="px-4">Clear</Button>
                            <Button variant="contained" onClick={applyMoreFilters} className="px-4">Filter</Button>
                          </div>

                        </div>
                      </Popover>
                    </>}
                    
                  </div>
              </div>
            </Box>
            <Box sx={{ paddingX: '20px' }}>
            {
              stateRegisterQue && stateRegisterQue.length <= 0 ?
                <Box sx={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant='h5'>No Records Found</Typography>
                </Box>
                :
                <>
                  <TableContainer sx={{ border: '1px solid #e6e6e6', marginTop: '10px', maxHeight: '385px', overflowY: 'scroll' }}>
                    <Table stickyHeader sx={{ minWidth: 650 }} aria-label="sticky table">
                      <TableHead sx={{ '.MuiTableCell-root': { backgroundColor: '#E7EEF7',fontWeight:'600' } }}>
                        <TableRow>
                          {/* <TableCell><Checkbox/></TableCell> */}
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'company_id'} direction={searchConfig.sortType} onClick={onClickSortCompany}>Company</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'associate_company_id'} direction={searchConfig.sortType} onClick={onClickSortAssociateCompany}>AssociateCompany</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'state_id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>State</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'location_id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Location</TableSortLabel></TableCell>
                          {showCLRAColumns && (
                            <>
                              <TableCell > <TableSortLabel active={searchConfig.activeSort === 'employer_location.location_name'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Employer</TableSortLabel></TableCell>
                              <TableCell > <TableSortLabel active={searchConfig.activeSort === 'employer_location.id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Employer Location</TableSortLabel></TableCell>
                              <TableCell > <TableSortLabel active={searchConfig.activeSort === 'sub_contractor'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Sub Contractor</TableSortLabel></TableCell>
                              <TableCell > <TableSortLabel active={searchConfig.activeSort === 'sub_contractor_id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Sub Contractor Location</TableSortLabel></TableCell>
                            </>
                          )}
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'location_id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Year</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'location_id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Month</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'year'} direction={searchConfig.sortType} onClick={onClickSortYear}>Que Id</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'year'} direction={searchConfig.sortType} onClick={onClickSortYear}>Que Status</TableSortLabel></TableCell>
                          <TableCell >Download</TableCell>
                          <TableCell >Created Date</TableCell>
                          <TableCell >Last Updated</TableCell>
                          {/* <TableCell >Completed Date</TableCell> */}

                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stateRegisterQue && stateRegisterQue.map((each: any, index: number) => (
                          <TableRow
                            key={index}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            {/* <TableCell><Checkbox/></TableCell> */}
                            <TableCell >{each.company && each.company.name ? each.company.name : 'NA'}</TableCell>
                            <TableCell >{each.associate_company && each.associate_company.name ? each.associate_company.name : 'NA'}</TableCell>
                            <TableCell >{each.state && each.state.name ? each.state.name : 'NA'}</TableCell>
                            <TableCell >{each.location && each.location.name ? each.location.name : 'NA'}</TableCell>

                            {showCLRAColumns && (
                              <>
                                <TableCell >{each.employer && each.employer.name ? each.employer.name : 'NA'}</TableCell>
                                <TableCell >{each.employer_location && each.employer_location.location_name ? each.employer_location.location_name : 'NA'}</TableCell>
                                <TableCell >{each.sub_contractor && each.sub_contractor.name ? each.sub_contractor.name : 'NA'}</TableCell>
                                <TableCell >{each.sub_contractor_location && each.sub_contractor_location.location_name ? each.sub_contractor_location.location_name : 'NA'}</TableCell>
                              </>
                            )}

                            <TableCell >{each.year && each.year ? each.year : 'NA'}</TableCell>
                            <TableCell >{each.month && each.month ? each.month : 'NA'}</TableCell>
                            <TableCell >{each.que_id && each.que_id ? each.que_id : 'NA'}</TableCell>
                            <TableCell >{each.que_status ? (QUE_STATUS_DISPLAY[each.que_status] || each.que_status) : 'NA'}</TableCell>
                            <TableCell >
                            <Box sx={{ display: 'flex',justifyContent: "center" }}>
                              {each.que_status === 'pdf_processing_partially_completed' || each.que_status === 'pdf_processing_completed' ? (
                                <Icon 
                                    style={{ color: '#0654AD', opacity: 1, pointerEvents: 'auto' }} 
                                    type="button" 
                                    name={'download'} 
                                    text={'download'} 
                                    action={() => onclickDownloadBoth(each)} 
                                />
                              ): (
                                <Icon
                                  style={{ color: '#0654AD', opacity: 0.5, pointerEvents: 'none' }}
                                  name={'download'}
                                  text={'download'}
                                />
                              )
                              }
                            </Box>
                            </TableCell>
                            <TableCell >{each.created_at && formatDate(each.created_at) ? formatDate(each.created_at) : 'NA'}</TableCell>
                            <TableCell >{each.completed_date && formatDate(each.completed_date) ? formatDate(each.completed_date) : 'NA'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    sx={{
                      '.MuiTablePagination-toolbar': {
                        backgroundColor: '#EFEBFE',
                        height: '30px',
                        display: 'flex',
                        justifyContent: 'flex-end'
                      },
                      '.MuiTablePagination-displayedRows': {
                        margin: '0',
                      },
                      '.MuiTablePagination-selectLabel': {
                        margin: '0',
                      },
                      '.MuiTablePagination-spacer': {
                        display: 'none '
                      },
                      '.MuiTablePagination-input': {
                        marginRight: 'auto'
                      },
                    }}

                    labelRowsPerPage='Show'
                    labelDisplayedRows={(page) =>
                      `Showing ${page.from}-${page.to === -1 ? page.count : page.to} of ${page.count}`
                    }
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={stateRegisterQueCount}
                    rowsPerPage={searchConfig.rowsPerPage}
                    page={searchConfig.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
            }
          </Box>
        </div>
      {loading && <PageLoader>Loading...</PageLoader>}
      {downloading && <PageLoader>Processing Registers...</PageLoader>}
    </div>
  )
}
export default InputsDashboard